<template>
  <div class="grey lighten-5" :class="$vuetify.breakpoint.xs ? 'pa-2' : 'pa-6'" style="min-height: 100vh;">
    <v-container style="max-width: 900px;">

      <!-- Botón volver -->
      <v-btn text color="deep-purple" class="mb-3" @click="$router.push('/eventos')">
        <v-icon left>mdi-arrow-left</v-icon> Volver a eventos
      </v-btn>

      <!-- Loading -->
      <div v-if="loading" class="text-center pa-8">
        <v-progress-circular indeterminate color="deep-purple"></v-progress-circular>
      </div>

      <!-- Error -->
      <v-alert v-else-if="error" type="error" text>
        {{ error }}
      </v-alert>

      <!-- Contenido del evento -->
      <v-card v-else-if="event" elevation="2">

        <!-- Imagen -->
        <v-img v-if="imageUrl" :src="imageUrl" aspect-ratio="1.5" contain
          class="grey lighten-3" style="cursor: pointer;" @click="fullscreenImage = true">
          <template v-slot:default>
            <div class="d-flex justify-end pa-2">
              <v-btn icon small dark class="grey darken-2" @click.stop="fullscreenImage = true">
                <v-icon small>mdi-fullscreen</v-icon>
              </v-btn>
            </div>
          </template>
        </v-img>

        <v-card-text class="pt-4">
          <!-- Chips -->
          <div class="mb-3">
            <v-chip small :color="tipoColor(event.type)" dark class="mr-1">
              <v-icon x-small left>{{ tipoIcon(event.type) }}</v-icon>
              {{ tipoLabel(event.type) }}
            </v-chip>
            <v-chip v-if="event.category" small :color="event.category.color || 'grey lighten-2'" class="mr-1">
              {{ event.category.name }}
            </v-chip>
            <v-chip small :color="temporalColor(event.temporal_status)" dark>
              {{ temporalLabel(event.temporal_status) }}
            </v-chip>
          </div>

          <!-- Título -->
          <h1 class="text-h5 font-weight-bold mb-3">{{ event.title }}</h1>

          <!-- Fechas -->
          <div class="mb-3">
            <p class="mb-1">
              <v-icon small class="mr-1">mdi-calendar</v-icon>
              <strong>Inicio:</strong> {{ formatDate(event.start_date) }}
            </p>
            <p v-if="event.end_date" class="mb-1">
              <v-icon small class="mr-1">mdi-calendar-end</v-icon>
              <strong>Fin:</strong> {{ formatDate(event.end_date) }}
            </p>
          </div>

          <v-divider class="mb-3"></v-divider>

          <!-- Descripción -->
          <div class="mb-3 body-1" style="white-space: pre-line;">{{ event.description }}</div>

          <!-- Contenido detallado (colapsable) -->
          <v-expansion-panels v-if="hasBody" flat class="mb-3">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="subtitle-2 grey--text text--darken-1">
                  <v-icon small class="mr-1">mdi-text-box-outline</v-icon>
                  Detalles del evento
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="body-1 ql-editor-content" v-html="event.body"></div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Enlace virtual -->
          <div v-if="event.virtual_url && (event.type === 'virtual' || event.type === 'hibrido')" class="mb-3">
            <v-divider class="mb-3"></v-divider>
            <p class="subtitle-2 mb-1">
              <v-icon small class="mr-1">mdi-video</v-icon> Enlace virtual
            </p>
            <v-btn small color="purple" dark :href="event.virtual_url" target="_blank">
              <v-icon left small>mdi-open-in-new</v-icon> Unirse a la reunión
            </v-btn>
          </div>

          <!-- Organizador -->
          <div v-if="event.organizer" class="mb-2">
            <v-divider class="mb-3"></v-divider>
            <p class="subtitle-2 mb-2">
              <v-icon small class="mr-1">mdi-account-group</v-icon> Organizador
            </p>

            <p class="font-weight-bold mb-1">{{ event.organizer.name }}</p>
            <p v-if="event.organizer.description" class="caption grey--text mb-2">{{ event.organizer.description }}</p>

            <!-- Contacto -->
            <div v-if="event.organizer.email || event.organizer.phone" class="mb-2">
              <span v-if="event.organizer.email" class="caption mr-3">
                <v-icon x-small class="mr-1">mdi-email</v-icon>{{ event.organizer.email }}
              </span>
              <span v-if="event.organizer.phone" class="caption">
                <v-icon x-small class="mr-1">mdi-phone</v-icon>{{ event.organizer.phone }}
              </span>
            </div>

            <!-- Redes sociales -->
            <div class="d-flex align-center">
              <v-btn v-if="event.organizer.url_web" icon small :href="event.organizer.url_web" target="_blank">
                <v-icon color="grey darken-2">mdi-web</v-icon>
              </v-btn>
              <v-btn v-if="event.organizer.url_instagram" icon small :href="event.organizer.url_instagram" target="_blank">
                <v-icon color="purple">mdi-instagram</v-icon>
              </v-btn>
              <v-btn v-if="event.organizer.url_facebook" icon small :href="event.organizer.url_facebook" target="_blank">
                <v-icon color="blue">mdi-facebook</v-icon>
              </v-btn>
              <v-btn v-if="event.organizer.url_youtube" icon small :href="event.organizer.url_youtube" target="_blank">
                <v-icon color="red">mdi-youtube</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <!-- Acciones: compartir -->
        <v-card-actions class="grey lighten-4 flex-wrap pa-3">
          <v-btn small color="blue darken-1" dark :href="googleCalendarUrl" target="_blank" class="ma-1">
            <v-icon :left="!$vuetify.breakpoint.xs" small>mdi-google</v-icon>
            <span v-if="!$vuetify.breakpoint.xs">Add Calendar</span>
          </v-btn>
          <v-btn small color="blue darken-3" dark :href="facebookShareUrl" target="_blank" class="ma-1">
            <v-icon :left="!$vuetify.breakpoint.xs" small>mdi-facebook</v-icon>
            <span v-if="!$vuetify.breakpoint.xs">Compartir Facebook</span>
          </v-btn>
          <v-btn small color="green darken-1" dark :href="whatsappShareUrl" target="_blank" class="ma-1">
            <v-icon :left="!$vuetify.breakpoint.xs" small>mdi-whatsapp</v-icon>
            <span v-if="!$vuetify.breakpoint.xs">Enviar a WhatsApp</span>
          </v-btn>
          <v-btn small outlined color="grey darken-2" class="ma-1" @click="copiarLink">
            <v-icon :left="!$vuetify.breakpoint.xs" small>mdi-link-variant</v-icon>
            <span v-if="!$vuetify.breakpoint.xs">Copiar link</span>
          </v-btn>
        </v-card-actions>
      </v-card>

    </v-container>

    <!-- Fullscreen image -->
    <v-dialog v-model="fullscreenImage" fullscreen transition="dialog-bottom-transition">
      <v-card dark class="d-flex align-center justify-center" style="background: #000;">
        <v-btn icon dark style="position: absolute; top: 16px; right: 16px; z-index: 1;" @click="fullscreenImage = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-img v-if="imageUrl" :src="imageUrl" contain max-height="100vh" max-width="100vw"></v-img>
      </v-card>
    </v-dialog>

    <!-- Snackbar link copiado -->
    <v-snackbar v-model="linkCopiado" :timeout="2000" color="success" top right>
      <v-icon left>mdi-check</v-icon> Link copiado al portapapeles
    </v-snackbar>
  </div>
</template>

<script>
import eventService from '@/services/eventService.js';

export default {
  name: 'EventDetailV',
  props: {
    slug: { type: String, required: true }
  },
  data() {
    return {
      event: null,
      loading: true,
      error: '',
      fullscreenImage: false,
      linkCopiado: false
    };
  },
  computed: {
    imageUrl() {
      if (this.event && this.event.image) {
        return eventService.getImageUrl(this.event.id, this.event.image);
      }
      return '';
    },
    hasBody() {
      if (!this.event || !this.event.body) return false;
      const stripped = this.event.body.replace(/<[^>]*>/g, '').trim();
      return stripped.length > 0;
    },
    eventUrl() {
      if (!this.event) return '';
      return window.location.origin + '/eventos/' + this.event.slug;
    },
    googleCalendarUrl() {
      if (!this.event) return '#';
      const formatDt = (dt) => {
        if (!dt) return '';
        return dt.replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z').replace(/\.\d+$/, '');
      };
      const start = formatDt(this.event.start_date);
      const end = this.event.end_date ? formatDt(this.event.end_date) : start;
      const details = (this.event.description || '').substring(0, 300)
        + '\n\nMás info: ' + this.eventUrl;
      const location = this.event.virtual_url || '';
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: this.event.title,
        dates: start + '/' + end,
        details: details,
        location: location,
      });
      return 'https://calendar.google.com/calendar/render?' + params.toString();
    },
    facebookShareUrl() {
      if (!this.event) return '#';
      return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(this.eventUrl);
    },
    whatsappShareUrl() {
      if (!this.event) return '#';
      const text = this.event.title + ' — ' + this.formatDate(this.event.start_date) + '\n' + this.eventUrl;
      return 'https://wa.me/?text=' + encodeURIComponent(text);
    }
  },
  mounted() {
    this.cargarEvento();
  },
  methods: {
    cargarEvento() {
      this.loading = true;
      this.error = '';
      eventService.getEvento(this.slug).then(res => {
        this.event = res.data;
      }).catch(err => {
        if (err.response && err.response.status === 404) {
          this.error = 'Evento no encontrado.';
        } else {
          this.error = 'Error al cargar el evento.';
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    copiarLink() {
      navigator.clipboard.writeText(this.eventUrl).then(() => {
        this.linkCopiado = true;
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('es-PY', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    tipoColor(type) {
      const map = { presencial: 'teal', virtual: 'purple', hibrido: 'indigo' };
      return map[type] || 'grey';
    },
    tipoIcon(type) {
      const map = { presencial: 'mdi-map-marker', virtual: 'mdi-video', hibrido: 'mdi-monitor-cellphone' };
      return map[type] || 'mdi-help';
    },
    tipoLabel(type) {
      const map = { presencial: 'Presencial', virtual: 'Virtual', hibrido: 'Híbrido' };
      return map[type] || type;
    },
    temporalColor(ts) {
      const map = { upcoming: 'green', ongoing: 'blue', past: 'grey' };
      return map[ts] || 'grey';
    },
    temporalLabel(ts) {
      const map = { upcoming: 'Próximo', ongoing: 'En curso', past: 'Finalizado' };
      return map[ts] || ts;
    }
  }
};
</script>

<style scoped>
.ql-editor-content a {
  color: #1976D2;
  text-decoration: underline;
}
.ql-editor-content ul,
.ql-editor-content ol {
  padding-left: 20px;
}
</style>
